import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  School as SchoolIcon,
  CalendarMonth as MonthIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface MonthlySchoolData {
  school_id: number;
  school_name: string;
  registration_number: string;
  district: string;
  region: string;
  student_rate: number;
  teacher_rate: number;
  reporting_days: number;
}

interface MonthlyData {
  month: string;
  schools_count: number;
  avg_student_rate: number;
  avg_teacher_rate: number;
  total_students: number;
  total_present: number;
  schools: MonthlySchoolData[];
}

interface MonthlyReportData {
  period: {
    start_date: string;
    end_date: string;
    months_analyzed: number;
  };
  monthly_summary: Record<string, MonthlyData>;
  school_trends: Record<string, any>;
  top_performing_schools: MonthlySchoolData[];
  bottom_performing_schools: MonthlySchoolData[];
}

const MonthlyAttendanceReport: React.FC = () => {
  const [reportData, setReportData] = useState<MonthlyReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [selectedDistrict, setSelectedDistrict] = useState<string>('');
  const [regions, setRegions] = useState<Array<{ id: number; name: string }>>([]);
  const [districts, setDistricts] = useState<Array<{ id: number; name: string; region__id: number; region__name: string }>>([]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchMonthlyReport();
  }, [selectedRegion, selectedDistrict]);

  const fetchFilterOptions = async () => {
    try {
      const { attendanceAPI } = await import('../../services/attendanceApi');
      const response = await attendanceAPI.getFilterOptions();
      if (response.data.status === 'success') {
        setRegions(response.data.data.regions || []);
        setDistricts(response.data.data.districts || []);
      }
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  };

  const filteredDistricts = selectedRegion
    ? districts.filter((d) => d.region__id?.toString() === selectedRegion || d.region__name === selectedRegion)
    : districts;

  const fetchMonthlyReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const { attendanceAPI } = await import('../../services/attendanceApi');

      const response = await attendanceAPI.getMonthlyReport({
        district: selectedDistrict || undefined,
      });

      if (response.data.status === 'success') {
        setReportData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load monthly attendance report');
      }
    } catch (err: any) {
      console.error('Monthly attendance report error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load monthly attendance report');
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (rate: number): 'success' | 'warning' | 'error' => {
    if (rate >= 95) return 'success';
    if (rate >= 85) return 'warning';
    return 'error';
  };

  // Prepare chart data
  const getChartData = () => {
    if (!reportData) return null;

    const months = Object.keys(reportData.monthly_summary).sort();
    const studentRates = months.map(month => reportData.monthly_summary[month].avg_student_rate);
    const teacherRates = months.map(month => reportData.monthly_summary[month].avg_teacher_rate);

    return {
      labels: months.map(month => {
        const date = new Date(month + '-01');
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }),
      datasets: [
        {
          label: 'Student Attendance %',
          data: studentRates,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1,
        },
        {
          label: 'Teacher Attendance %',
          data: teacherRates,
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Monthly Attendance Trends',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
      },
    },
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No monthly attendance data available
      </Alert>
    );
  }

  const chartData = getChartData();

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Monthly Attendance Report by Schools
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Region</InputLabel>
                <Select
                  value={selectedRegion}
                  label="Region"
                  onChange={(e) => setSelectedRegion(e.target.value)}
                >
                  <MenuItem value="">All Regions</MenuItem>
                  {regions.map((region) => (
                    <MenuItem key={region.id} value={region.name}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>District</InputLabel>
                <Select
                  value={selectedDistrict}
                  label="District"
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                >
                  <MenuItem value="">All Districts</MenuItem>
                  {filteredDistricts.map((district) => (
                    <MenuItem key={district.id} value={district.name}>
                      {district.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={fetchMonthlyReport}
                fullWidth
              >
                Refresh Report
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Period Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Report Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">Period</Typography>
              <Typography variant="h6">
                {reportData.period.start_date} to {reportData.period.end_date}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">Months Analyzed</Typography>
              <Typography variant="h6">{reportData.period.months_analyzed}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="textSecondary">Schools Tracked</Typography>
              <Typography variant="h6">
                {Object.values(reportData.monthly_summary)[0]?.schools_count || 0}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Monthly Trend Chart */}
      {chartData && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Line data={chartData} options={chartOptions} />
          </CardContent>
        </Card>
      )}

      {/* Top & Bottom Performers */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="success.main">
                <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Top Performing Schools
              </Typography>
              {reportData.top_performing_schools.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>School</TableCell>
                        <TableCell align="right">Student Rate</TableCell>
                        <TableCell align="right">Teacher Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.top_performing_schools.slice(0, 5).map((school, index) => (
                        <TableRow key={index}>
                          <TableCell>{school.school_name}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${school.student_rate}%`}
                              color={getPerformanceColor(school.student_rate)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${school.teacher_rate}%`}
                              color={getPerformanceColor(school.teacher_rate)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="error.main">
                <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1, transform: 'rotate(180deg)' }} />
                Schools Needing Support
              </Typography>
              {reportData.bottom_performing_schools.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>School</TableCell>
                        <TableCell align="right">Student Rate</TableCell>
                        <TableCell align="right">Teacher Rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.bottom_performing_schools.slice(0, 5).map((school, index) => (
                        <TableRow key={index}>
                          <TableCell>{school.school_name}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${school.student_rate}%`}
                              color={getPerformanceColor(school.student_rate)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${school.teacher_rate}%`}
                              color={getPerformanceColor(school.teacher_rate)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No data available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Monthly Details */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Monthly Breakdown by Schools
          </Typography>

          {Object.entries(reportData.monthly_summary)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([monthKey, monthData]) => (
              <Accordion key={monthKey}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" width="100%">
                    <MonthIcon sx={{ mr: 2 }} />
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      {new Date(monthKey + '-01').toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric'
                      })}
                    </Typography>
                    <Chip
                      label={`${monthData.avg_student_rate.toFixed(1)}% avg`}
                      color={getPerformanceColor(monthData.avg_student_rate)}
                      size="small"
                      sx={{ mr: 2 }}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {monthData.schools_count} schools
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>School Name</TableCell>
                          <TableCell>Registration</TableCell>
                          <TableCell>District</TableCell>
                          <TableCell align="right">Student Rate</TableCell>
                          <TableCell align="right">Teacher Rate</TableCell>
                          <TableCell align="right">Days Reported</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {monthData.schools
                          .sort((a, b) => b.student_rate - a.student_rate)
                          .map((school, index) => (
                            <TableRow key={index} hover>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                                  {school.school_name}
                                </Box>
                              </TableCell>
                              <TableCell>{school.registration_number}</TableCell>
                              <TableCell>{school.district}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${school.student_rate}%`}
                                  color={getPerformanceColor(school.student_rate)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${school.teacher_rate}%`}
                                  color={getPerformanceColor(school.teacher_rate)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="right">{school.reporting_days}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MonthlyAttendanceReport;